import type { AuditReport } from '@internal-audit/domain';
import { useGetAuditReportsByDateRange } from '@internal-audit/hooks/useGetAuditReportsByDateRange';
import { useGetComprehensiveAnalytics } from '@internal-audit/hooks/useGetComprehensiveAnalytics';
import { useMemo } from 'react';

// Infer type from hook return value
type ComprehensiveAnalytics = ReturnType<typeof useGetComprehensiveAnalytics>['comprehensiveAnalytics'];

export interface AuditAnalyticsData {
  auditReports: AuditReport[];
  comprehensiveAnalytics: ComprehensiveAnalytics;
  isLoading: boolean;
}

interface UseAuditAnalyticsDataProps {
  communityId: string | undefined;
  fallbackAuditReports?: AuditReport[];
  skip?: boolean;
}

/**
 * Custom hook to fetch audit analytics data (reports and comprehensive analytics)
 * Can be used by both header (V1) and page body (V2) layouts
 */
export function useAuditAnalyticsData({ communityId, fallbackAuditReports, skip = false }: UseAuditAnalyticsDataProps): AuditAnalyticsData {
  // Calculate date range for the last 1 year to get meaningful chart data
  // Date range is intentionally computed once on mount. The "last 1 year" window
  // is stable for the typical page lifecycle. If the component stays mounted for
  // extended periods, the range remains fixed to the mount time, which is acceptable
  // for audit analytics purposes.
  const dateRange = useMemo(() => {
    const toDate = new Date();
    const fromDate = new Date();

    fromDate.setFullYear(fromDate.getFullYear() - 1); // Last 1 year

    return {
      fromDate: fromDate.toISOString(),
      toDate: toDate.toISOString(),
    };
  }, []);

  // Fetch audit reports by date range for the chart
  const { auditReports: auditReportsByDateRange = [], loading: auditReportsLoading } = useGetAuditReportsByDateRange({
    communityId,
    fromDate: dateRange.fromDate,
    toDate: dateRange.toDate,
    options: {
      skip: skip || !communityId,
    },
  });

  // Fetch comprehensive analytics for the same time period
  const { comprehensiveAnalytics, isLoading: analyticsIsLoading } = useGetComprehensiveAnalytics({
    communityId: communityId || '',
    fromDate: dateRange.fromDate,
    toDate: dateRange.toDate,
    skip: skip || !communityId,
  });

  // Coordinate loading states
  const isLoading = auditReportsLoading || analyticsIsLoading;

  // Use fallback only when query is skipped (no communityId or explicitly skipped)
  // Otherwise use query result even if empty (empty is a valid state for new communities)
  const auditReports = skip || !communityId ? fallbackAuditReports || [] : auditReportsByDateRange;

  return {
    auditReports,
    comprehensiveAnalytics,
    isLoading,
  };
}
